import React, { useState, useEffect } from "react";
import { btnP, crdEl, sp, rad, bdg, icBox } from "../../constants/styles";
import { api } from "../../api";
import { formatPrice } from "../../utils/plans";

// Lazily inject the Cashfree v3 checkout SDK — only when the server
// selected Cashfree for this order (so Razorpay-only deployments never
// load it). Resolves once window.Cashfree is available.
function loadCashfreeSdk() {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve();
    const SRC = "https://sdk.cashfree.com/js/v3/cashfree.js";
    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      if (window.Cashfree) return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Cashfree SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = SRC; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Cashfree SDK failed to load"));
    document.head.appendChild(s);
  });
}

// Client-side mirror of server's computeBill. Lives here (not in
// utils/) because UpgradeModal is the only consumer right now and the
// formula is small. If a second consumer shows up, extract it.
function computePerPropertyBill(plan, propertyCount, cycle) {
  const n = Math.max(0, parseInt(propertyCount, 10) || 0);
  if (n === 0) return 0;
  const isYearly = cycle === "yearly";
  const base = isYearly ? plan.pp_unit_price_yearly : plan.pp_unit_price_monthly;
  const bulkConfigured = plan.pp_bulk_threshold > 0 &&
    (isYearly ? plan.pp_bulk_unit_price_yearly : plan.pp_bulk_unit_price_monthly) != null;
  const bulk = bulkConfigured
    ? (isYearly ? plan.pp_bulk_unit_price_yearly : plan.pp_bulk_unit_price_monthly)
    : base;
  const floor = isYearly ? (plan.pp_min_yearly || 0) : (plan.pp_min_monthly || 0);
  let total;
  if (!bulkConfigured || n <= plan.pp_bulk_threshold) {
    total = n * base;
  } else if (plan.pp_bulk_mode === "flat") {
    total = n * bulk;
  } else {
    // graduated (default)
    total = (plan.pp_bulk_threshold * base) + ((n - plan.pp_bulk_threshold) * bulk);
  }
  return Math.max(total, floor);
}

/**
 * Dynamic Upgrade Modal — fetches plans from API and lets user pick one.
 * Props: { h, t, used, limit, currentPlan, onClose, onUpgradeSuccess }
 */
export default function UpgradeModal({ h, t, used, limit, currentPlan, onClose, onUpgradeSuccess, propertyCount }) {
  // `propertyCount` is the org's current property count. Required for
  // per-property plans to render the live "N × ₹X = ₹total" math; we
  // fall back to `used` (the property usage indicator) which is the
  // same number for landlord callers.
  const propCount = (propertyCount != null) ? propertyCount : (used || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [discPct, setDiscPct] = useState(0);
  // Active campaign discount — shows the offer price at checkout. The SAME %
  // is applied for real, server-side, at order creation, so shown == charged.
  const disc = (paise) => (discPct > 0 ? Math.max(100, Math.round((paise || 0) * (100 - discPct) / 100)) : (paise || 0));
  // 'monthly' | 'yearly'. Yearly is only enabled when the selected plan
  // has a price_yearly set; we silently fall back to monthly otherwise.
  const [cycle, setCycle] = useState('monthly');

  // Fetch plans from API
  useEffect(() => {
    api.getPlans().then(d => {
      setDiscPct((d && d.campaignDiscountPercent) || 0);
      if (d.success && d.plans) {
        // Filter to paid plans only, sorted by sort_order
        const paid = (d.plans || []).filter(p => p.price > 0 && p.is_active);
        setPlans(paid);
        // Auto-select recommended plan (first plan above current, or first paid)
        const currentSort = d.plans.find(p => p.id === (currentPlan || "free"))?.sort_order ?? -1;
        const recommended = paid.find(p => p.sort_order > currentSort) || paid[0];
        if (recommended) setSelectedPlan(recommended.id);
      }
      setLoadingPlans(false);
    }).catch(() => setLoadingPlans(false));
  }, [currentPlan]);

  const handleUpgrade = async () => {
    if (loading || success || !selectedPlan) return;
    setLoading(true);
    setError(null);

    try {
      // If the user picked yearly but the plan no longer offers it
      // (e.g. admin removed price_yearly between selection and tap),
      // fall back to monthly silently — server will accept that.
      const sel = plans.find(p => p.id === selectedPlan);
      const effectiveCycle = (cycle === 'yearly' && sel?.price_yearly && sel.price_yearly > 0) ? 'yearly' : 'monthly';
      const orderData = await api.createBillingOrder(selectedPlan, effectiveCycle);

      if (!orderData || !orderData.success) {
        // Server-side blocks: show actual error to the user — do NOT
        // fake a success state, since no payment / renewal occurred.
        const code = orderData?.error;
        const msg = code === 'downgrade_not_allowed'
          ? "You can't downgrade to a lower plan. Contact support to switch."
          : code === 'already_pro'
            ? "You're already on this plan."
            : (code || t.billingOrderError || "Could not create order. Please try again.");
        setError(msg);
        setLoading(false);
        return;
      }

      // ── Cashfree checkout (when the server selected Cashfree) ──
      // Cashfree returns no client-side signature; after its modal
      // closes we confirm with the server, which fetches the real
      // payment status from Cashfree. The server is the source of truth.
      if (orderData.provider === 'cashfree') {
        try {
          await loadCashfreeSdk();
        } catch (_) {
          setError(t.billingScriptError || "Payment service unavailable. Check your internet connection.");
          setLoading(false);
          return;
        }
        if (!window.Cashfree || !orderData.payment_session_id) {
          setError(t.billingOrderError || "Could not start checkout. Please try again.");
          setLoading(false);
          return;
        }
        const cf = window.Cashfree({ mode: orderData.cashfree_mode === 'production' ? 'production' : 'sandbox' });
        // Full-page redirect (NOT the popup iframe). Cashfree's _modal
        // iframe gets denied browser features (encrypted-media for card
        // 3-DS) by the page's Feature Policy → "No available adapters" →
        // every payment method fails. Redirecting the whole tab to
        // Cashfree's own page sidesteps all iframe permission limits.
        // After payment Cashfree returns to our order's return_url
        // (/api/billing/checkout-return?provider=cashfree&order_id=…),
        // which verifies server-side, activates the plan, and bounces
        // back to the app with ?upgrade=success.
        const result = await cf.checkout({ paymentSessionId: orderData.payment_session_id, redirectTarget: '_self' });
        // With _self the page normally navigates away before this runs.
        // If it DOES return (e.g. the SDK couldn't redirect), surface it.
        if (result && result.error) {
          console.error('[Billing] Cashfree checkout error:', result.error);
          setError(t.billingOrderError || "Could not open Cashfree checkout. Please try again.");
          setLoading(false);
        }
        return;
      }

      // Razorpay has been retired from the checkout UI - Cashfree is the
      // only online provider now. If the server ever returns a non-Cashfree
      // order (e.g. platform Cashfree keys missing), fail gracefully instead
      // of opening a checkout we no longer ship. The server-side Razorpay
      // fallback + historical records stay intact (staged cutover).
      setError(t.billingUnavailable || "Online payment is temporarily unavailable. Please try again shortly or contact support.");
      setLoading(false);
      return;
    } catch (e) {
      console.error("Upgrade error:", e);
      setError(e.message || "Something went wrong");
      setLoading(false);
    }
  };

  const usagePct = limit > 0 && limit < 999999 ? Math.min((used / limit) * 100, 100) : 0;
  const sel = plans.find(p => p.id === selectedPlan);
  // Show the cycle toggle when at least one offered plan supports
  // yearly. Per-property plans expose yearly via `pp_unit_price_yearly`
  // instead of `price_yearly`, so check both.
  const hasYearly = (p) => (p.price_yearly && p.price_yearly > 0) ||
    (p.pricing_model === 'per_property' && p.pp_unit_price_yearly && p.pp_unit_price_yearly > 0);
  const yearlyAvailable = plans.some(hasYearly);
  // The selected plan may not have a yearly option even when others do —
  // in that case the CTA falls back to monthly for that plan.
  const selHasYearly = !!(sel && hasYearly(sel));

  const FEATURE_LABELS = {
    basic_dashboard: "Dashboard", payments: "Rent Payments", maintenance: "Maintenance",
    whatsapp: "WhatsApp Notifications", leases: "Lease Management", service_persons: "Service Persons",
    agreements: "Rental Agreements", commercial: "Commercial Properties", reports: "Reports",
    csv_export: "CSV Export", analytics: "Analytics", automation: "Automation",
    multi_user: "Multi-User Access", priority_support: "Priority Support"
  };

  return <div className="animate-fadeIn" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: h.ov, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: sp.lg }} onClick={onClose}>
    <div className="animate-scaleIn" style={{ ...crdEl(h), padding: sp.xl, maxWidth: 380, width: "100%", boxShadow: h.s3, maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>

      {/* Success state \u2014 dynamic title shows the actual plan and the
          right verb (Renewed vs Upgraded) instead of a hardcoded "Pro". */}
      {success ? <div style={{ textAlign: "center", padding: `${sp.xl}px 0` }}>
        <div style={{ width: 56, height: 56, borderRadius: 28, background: `${h.gn}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", marginBottom: sp.md }}>
          <span style={{ fontSize: 28, color: h.gn }}>{"\u2713"}</span>
        </div>
        <h3 style={{ color: h.gn, fontSize: 18, fontWeight: 700, margin: 0 }}>
          {sel?.id === currentPlan
            ? `${sel?.name || ""} Renewed!`
            : `Upgraded to ${sel?.name || "Pro"}!`}
        </h3>
        <p style={{ color: h.ts, fontSize: 12, margin: `${sp.sm}px 0 0` }}>{sel?.name || "Pro"} Plan Active</p>
      </div> : <>

        {/* Header \u2014 title falls back to "Choose Your Plan" when the
            cached locale string is the legacy "Upgrade to Pro" (which
            no longer matches reality once per-property plans exist). */}
        <div style={{ textAlign: "center", marginBottom: sp.lg }}>
          <div style={{ width: 48, height: 48, borderRadius: rad.md, background: `${h.ac}12`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", marginBottom: sp.sm }}>
            <span style={{ fontSize: 24 }}>{"\uD83D\uDE80"}</span>
          </div>
          <h3 style={{ color: h.tx, fontSize: 17, fontWeight: 700, margin: 0 }}>
            {t.planUpgradeTitle || "Choose Your Plan"}
          </h3>
          <p style={{ color: h.ts, fontSize: 12, margin: `${sp.xs}px 0 0` }}>
            {t.planChooseDesc || "Upgrade, renew, or change your plan"}
          </p>
        </div>

        {/* Usage indicator */}
        {usagePct > 0 && <div style={{ background: h.sf, borderRadius: rad.sm, padding: sp.md, marginBottom: sp.md }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: sp.xs }}>
            <span style={{ color: h.ts, fontSize: 11 }}>{t.planPropsUsed || "Properties used"}</span>
            <span style={{ color: h.tx, fontWeight: 700, fontSize: 13 }}>{used}<span style={{ color: h.ts, fontWeight: 400 }}> / {limit}</span></span>
          </div>
          <div style={{ height: 5, borderRadius: 3, background: h.ib, overflow: "hidden" }}>
            <div style={{ width: `${usagePct}%`, height: "100%", borderRadius: 3, background: usagePct >= 90 ? h.rd : usagePct >= 70 ? h.wn : h.ac, transition: "width 0.5s ease" }} />
          </div>
        </div>}

        {/* Monthly / Yearly cycle toggle — only when at least one plan
            offers yearly pricing. Picking yearly on a plan that doesn't
            have price_yearly set falls back to monthly server-side. */}
        {yearlyAvailable && !loadingPlans && <div style={{
          display: "flex", background: h.ib, borderRadius: rad.md,
          padding: 3, marginBottom: sp.md, border: `1px solid ${h.bd}`,
        }}>
          {[
            { id: 'monthly', label: 'Monthly' },
            { id: 'yearly', label: 'Yearly' },
          ].map(opt => {
            const on = cycle === opt.id;
            return <button key={opt.id} onClick={() => setCycle(opt.id)} style={{
              flex: 1, padding: `${sp.xs + 2}px ${sp.sm}px`,
              borderRadius: rad.sm, border: "none",
              background: on ? h.cd : "transparent",
              color: on ? h.tx : h.ts,
              fontWeight: on ? 700 : 500, fontSize: 12,
              cursor: "pointer", transition: "all 0.15s",
              boxShadow: on ? `0 1px 3px rgba(0,0,0,0.08)` : "none",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              {opt.label}
              {opt.id === 'yearly' && (() => {
                // Show "Save up to N%" hint for the best discount across visible plans
                const bestPct = plans.reduce((max, p) => {
                  if (!p.price_yearly || p.price_yearly <= 0 || !p.price) return max;
                  const full = p.price * 12;
                  const pct = Math.round((1 - p.price_yearly / full) * 100);
                  return pct > max ? pct : max;
                }, 0);
                return bestPct > 0 ? <span style={{
                  background: h.gn, color: "#fff",
                  padding: "1px 6px", borderRadius: 999,
                  fontSize: 9, fontWeight: 700,
                }}>Save {bestPct}%</span> : null;
              })()}
            </button>;
          })}
        </div>}

        {/* Plans loading */}
        {loadingPlans ? <div style={{ textAlign: "center", padding: sp.xl }}>
          <span style={{ color: h.ts, fontSize: 12 }}>Loading plans...</span>
        </div> : <>

          {/* Plan cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: sp.sm + 4, marginBottom: sp.md }}>
            {(() => {
              // Determine the one recommended plan: next tier above current plan
              const currentSort = plans.find(p => p.id === currentPlan)?.sort_order ?? -1;
              const recommendedId = (plans.find(p => p.sort_order > currentSort) || plans[0])?.id;
              return plans.map(p => {
                const isSelected = selectedPlan === p.id;
                const isRecommended = p.id === recommendedId;
                const isCurrent = p.id === currentPlan;
                return <div key={p.id} onClick={() => setSelectedPlan(p.id)} style={{
                  padding: sp.md, borderRadius: rad.md,
                  border: `2px solid ${isSelected ? (isCurrent ? h.gn : h.ac) : h.bd}`,
                  background: isSelected ? (isCurrent ? `${h.gn}08` : `${h.ac}08`) : "transparent",
                  cursor: "pointer", position: "relative", transition: "all 0.2s",
                  opacity: 1, overflow: "visible"
                }}>
                  {isRecommended && !isCurrent && <span style={{
                    display: "inline-flex", alignItems: "center",
                    background: h.gn, color: "#fff",
                    padding: "3px 12px", borderRadius: 999, fontSize: 9, fontWeight: 700,
                    letterSpacing: 0.3, position: "absolute", top: -11, right: sp.md,
                    zIndex: 2, boxShadow: `0 2px 6px ${h.gn}30`
                  }}>RECOMMENDED</span>}
                  {isCurrent && <span style={{
                    display: "inline-flex", alignItems: "center",
                    background: isSelected ? h.gn : (h.cd || "#fff"), color: isSelected ? "#fff" : h.sc,
                    padding: "3px 12px", borderRadius: 999, fontSize: 9, fontWeight: 700,
                    position: "absolute", top: -11, right: sp.md,
                    zIndex: 2, border: isSelected ? "none" : `1.5px solid ${h.bd}`,
                    boxShadow: isSelected ? `0 2px 6px ${h.gn}30` : "none"
                  }}>{isSelected ? "RENEW" : "CURRENT"}</span>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: isCurrent ? h.sc : h.tx, fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                      {p.pricing_model === 'per_property' ? (
                        <div style={{ color: h.ts, fontSize: 11, marginTop: 2 }}>
                          Pay per property · Unlimited
                        </div>
                      ) : (
                        <div style={{ color: h.ts, fontSize: 11, marginTop: 2 }}>
                          {p.max_properties >= 999999 ? "Unlimited" : `Up to ${p.max_properties}`} properties
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {p.pricing_model === 'per_property' ? (() => {
                        // Per-property plan: render LIVE total based on
                        // the org's current property count. Show the unit
                        // price as the subtitle so the math is obvious.
                        const cycleHere = (cycle === 'yearly' && p.pp_unit_price_yearly > 0) ? 'yearly' : 'monthly';
                        const total = computePerPropertyBill(p, propCount, cycleHere);
                        const unit = cycleHere === 'yearly' ? p.pp_unit_price_yearly : p.pp_unit_price_monthly;
                        const bulkUnit = cycleHere === 'yearly' ? p.pp_bulk_unit_price_yearly : p.pp_bulk_unit_price_monthly;
                        const inBulk = p.pp_bulk_threshold > 0 && propCount > p.pp_bulk_threshold && bulkUnit;
                        return <>
                          <div style={{ color: isCurrent ? h.sc : isSelected ? h.ac : h.tx, fontWeight: 800, fontSize: 18 }}>
                            {formatPrice(total)}
                          </div>
                          <div style={{ color: h.tt, fontSize: 10 }}>
                            {cycleHere === 'yearly' ? '/year' : '/month'}
                          </div>
                          <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>
                            {propCount} prop × {formatPrice(inBulk ? bulkUnit : unit)}
                          </div>
                          {inBulk && (
                            <div style={{ color: h.gn, fontSize: 9, fontWeight: 700, marginTop: 1 }}>
                              Volume rate applied
                            </div>
                          )}
                          {!inBulk && p.pp_bulk_threshold > 0 && bulkUnit && (
                            <div style={{ color: h.ac, fontSize: 9, fontWeight: 600, marginTop: 1 }}>
                              {p.pp_bulk_threshold - propCount + 1}+ props → {formatPrice(bulkUnit)}/each
                            </div>
                          )}
                        </>;
                      })() : (() => {
                        // Fixed-tier plan: existing flat-price render.
                        const pHasYearly = p.price_yearly && p.price_yearly > 0;
                        const showYearly = cycle === 'yearly' && pHasYearly;
                        const displayPrice = showYearly ? p.price_yearly : p.price;
                        return <>
                          <div style={{ color: isCurrent ? h.sc : isSelected ? h.ac : h.tx, fontWeight: 800, fontSize: 18 }}>{formatPrice(displayPrice)}</div>
                          <div style={{ color: h.tt, fontSize: 10 }}>
                            {showYearly ? '/year (365 days)' : '/month (30 days)'}
                          </div>
                          {showYearly && p.price > 0 && (() => {
                            const fullYear = p.price * 12;
                            const pct = Math.round((1 - p.price_yearly / fullYear) * 100);
                            return pct > 0 ? <div style={{ color: h.gn, fontSize: 9, fontWeight: 700, marginTop: 2 }}>Save {pct}%</div> : null;
                          })()}
                          {cycle === 'yearly' && !pHasYearly && <div style={{ color: h.wn, fontSize: 9, fontWeight: 600, marginTop: 2 }}>Monthly only</div>}
                        </>;
                      })()}
                    </div>
                  </div>
                </div>;
              });
            })()}
          </div>

          {/* Selected plan features */}
          {sel && <div style={{ marginBottom: sp.md }}>
            <div style={{ color: h.tx, fontSize: 11, fontWeight: 600, marginBottom: sp.sm }}>{sel.name} includes:</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${sp.xs}px ${sp.sm}px` }}>
              {(sel.features || []).map((f, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
                <span style={{ color: h.gn, fontSize: 10, fontWeight: 700 }}>{"\u2713"}</span>
                <span style={{ color: h.tx, fontSize: 11 }}>{FEATURE_LABELS[f] || f.replace(/_/g, " ")}</span>
              </div>)}
            </div>
          </div>}
        </>}

        {/* Error */}
        {error && <div style={{ background: `${h.rd}10`, border: `1px solid ${h.rd}20`, borderRadius: rad.sm, padding: sp.sm, marginBottom: sp.sm }}>
          <p style={{ color: h.rd, fontSize: 11, margin: 0 }}>{error}</p>
        </div>}

        {/* Campaign offer — the discounted amount below is what actually gets
            charged (the same % is applied server-side at order creation). */}
        {discPct > 0 && sel && (() => {
          let orig;
          if (sel.pricing_model === 'per_property') {
            const ch = (cycle === 'yearly' && sel.pp_unit_price_yearly > 0) ? 'yearly' : 'monthly';
            orig = computePerPropertyBill(sel, propCount, ch);
          } else {
            const sy = cycle === 'yearly' && selHasYearly;
            orig = sy ? sel.price_yearly : sel.price;
          }
          return <div style={{ background: `${h.gn}10`, border: `1px solid ${h.gn}30`, borderRadius: rad.sm, padding: sp.sm, marginBottom: sp.sm, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>{"🎉"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: h.gn, fontWeight: 700, fontSize: 12 }}>{discPct}% offer applied</div>
              <div style={{ color: h.ts, fontSize: 11 }}>
                <span style={{ textDecoration: "line-through", marginRight: 6 }}>{formatPrice(orig)}</span>
                <b style={{ color: h.tx }}>{formatPrice(disc(orig))}</b> {t.atCheckout || "at checkout"}
              </div>
            </div>
          </div>;
        })()}

        {/* CTA */}
        <button onClick={handleUpgrade} disabled={loading || !selectedPlan}
          style={{
            ...btnP(h), width: "100%", padding: sp.md, fontSize: 14, fontWeight: 700,
            background: loading ? h.sc : h.ac, marginBottom: sp.xs,
            opacity: loading ? 0.7 : 1, cursor: loading ? "wait" : "pointer",
            boxShadow: loading ? "none" : `0 4px 16px ${h.ac}30`
          }}>
          {loading ? (t.billingProcessing || "Processing...")
            : sel ? (() => {
                const verb = sel.id === currentPlan ? 'Renew' : 'Upgrade to';
                if (sel.pricing_model === 'per_property') {
                  // Per-property: live total = unit \u00d7 count (or bulk rate)
                  const cycleHere = (cycle === 'yearly' && sel.pp_unit_price_yearly > 0) ? 'yearly' : 'monthly';
                  const total = computePerPropertyBill(sel, propCount, cycleHere);
                  const suffix = cycleHere === 'yearly' ? '/yr' : '/mo';
                  return `${verb} ${sel.name} \u2014 ${formatPrice(disc(total))}${suffix}`;
                }
                const showYearly = cycle === 'yearly' && selHasYearly;
                const amt = showYearly ? sel.price_yearly : sel.price;
                const suffix = showYearly ? '/yr' : '/mo';
                return `${verb} ${sel.name} \u2014 ${formatPrice(disc(amt))}${suffix}`;
              })() : "Select a Plan"}
        </button>

        <div style={{ textAlign: "center", marginBottom: sp.xs, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          {/* Inline lock SVG \u2014 consistent with the rest of the app's
              icon language, renders identically across platforms. */}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: h.tt }} aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span style={{ color: h.tt, fontSize: 10 }}>{t.billingSecureGeneric || "Secured & encrypted checkout"}</span>
        </div>

        <button onClick={onClose} style={{ background: "none", border: "none", color: h.ts, width: "100%", padding: sp.sm, cursor: "pointer", fontSize: 12 }}>
          {t.planMaybeLater || "Maybe Later"}
        </button>
      </>}
    </div>
  </div>;
}
